import { Accepted, CONSENT_VERSION, Cookie, Cookies, consentVersionCookie, cookies, deleteCookie, getCookie, requiredCookies, setCookie } from "./cookies";
import { DataLayer } from "./datalayer";
import { ConsentMapping, FullConsentUpdate, defaultContent as defaultConsent, gtag, updateConsentMode } from "./datalayer.consent";
import {
  ConsentMapping as SqzlConsentMapping,
  FullConsentUpdate as SqzlFullConsentUpdate,
  sqzl,
  updateConsent as updateConsentSqzl,
  defaultConsent as sqzlDefaultConsent
} from "./sqzl.consent";

export const cookieToModeMapping: ConsentMapping<Cookie> = {
  'analytical-cookies': ['analytics_storage'],
  'functional-cookies': ['functionality_storage', 'security_storage'],
  'video-cookies': [],
  'tracking-cookies': ['personalization_storage', 'ad_personalization', 'ad_storage', 'ad_user_data'],
  // 'readspeaker-cookies': []
}

export const cookieToSqzlMapping: SqzlConsentMapping<Cookie> = {
  'analytical-cookies': ['analytics'],
  'functional-cookies': [],
  'video-cookies': [],
  'tracking-cookies': ['marketing']
}

export const getCurrentCookies: () => Map<Cookie, Accepted> = () =>
  new Map(cookies.map((cookie) => [cookie, getCookie(cookie) === 'yes' || requiredCookies.includes(cookie) ? 'yes' : 'no']))

export const updateConsent = updateConsentMode(cookieToModeMapping)
export const updateSqzlConsent = updateConsentSqzl(cookieToSqzlMapping)

export const setConsentCookie = (cookie: Cookie, isAccepted: Accepted) => {
  updateConsent(cookie, isAccepted == 'yes')
  updateSqzlConsent(cookie, isAccepted == 'yes')
  setCookie(cookie, isAccepted, 1000)
}

export const acceptAllCookies = () => {
  // Set all configurable cookies to 'yes'
  cookies.forEach((cookie) => setConsentCookie(cookie, "yes"))
  // Fire datalayer event about accepting cookies
  DataLayer.acceptCookies();
  // Prevent cookie bar from showing again
  setCookie('showCookies', 'no', 1000)
  location.reload();
}

export const denyAllCookies = () => {
  // Set all but required configurable cookies to 'no'
  cookies.filter((cookie) => !requiredCookies.includes(cookie)).map((cookie) => setConsentCookie(cookie, 'no'))
  // Set required cookies to 'yes'
  requiredCookies.forEach((cookie) => setConsentCookie(cookie, "yes"))
  // Fire datalayer event about accepting cookies
  DataLayer.rejectCookies();
  // Prevent cookie bar from showing again
  setCookie('showCookies', 'no', 1000)
  location.reload();
}

export const acceptSelectedCookies = (selectedCookies: Cookies) => {
  // Grant selected consent types
  selectedCookies.forEach((isAccepted, cookie) => setConsentCookie(cookie, isAccepted))
  // Fire datalayer event about accepting cookies
  DataLayer.partialAcceptCookies();
  // Prevent cookie bar from showing again
  setCookie('showCookies', 'no', 1000)
  // Set required cookies to 'yes'
  requiredCookies.forEach((cookie) => setCookie(cookie, "yes", 1000))
  location.reload();
}

/**
 * Force visitors who consented under an older consent version to consent again.
 *
 * When the meaning of consent changes (e.g. new cookie statement terms or new tools
 * like Spotler Activate), bumping CONSENT_VERSION invalidates previously stored consent.
 * This clears the per-cookie flags and the `showCookies` flag so the cookie bar
 * reappears, and stamps the current version so it only happens once per bump.
 * Consent replayed to GTM/Squeezely afterwards falls back to the defaults (denied),
 * so no stale grant is re-applied.
 *
 * Must run before `updateInitialConsent()` pushes consent to the datalayer.
 */
export function resetConsentIfOutdated () {
  if (getCookie(consentVersionCookie) === CONSENT_VERSION) {
    return
  }
  // Drop every stored consent flag and the "bar dismissed" flag.
  cookies.forEach((cookie) => deleteCookie(cookie))
  deleteCookie('showCookies')
  // Stamp the current version so this only runs once per visitor per version bump.
  setCookie(consentVersionCookie, CONSENT_VERSION, 1000)
}

/**
 * Update the initial consent for Google consent mode v2
 * Additionally updates Squeezely consent if Squeezely is present
 */
export function updateInitialConsent () {
  resetConsentIfOutdated()
  const currentCookies = getCurrentCookies()
  const data: FullConsentUpdate = defaultConsent
  currentCookies.forEach((isAccepted, cookie) => {
    var modes = cookieToModeMapping[cookie]
    modes.forEach(mode => data[mode] = isAccepted == 'yes' ? 'granted' : 'denied')
  })
  gtag('consent', 'update', data);

  // Also update Squeezely consent
  const sqzlData: SqzlFullConsentUpdate = sqzlDefaultConsent
  currentCookies.forEach((isAccepted, cookie) => {
    var modes = cookieToSqzlMapping[cookie]
    if (modes.length > 0 && isAccepted == 'yes') {
      if (sqzlData.consent == 'revoke') sqzlData.consent = 'grant'
      sqzlData.permissions.push(...modes)
    }
  })
  if (sqzlData.consent == "grant" && sqzlData.permissions.includes('marketing')) {
    sqzlData.anonymize = "no"
  }
  sqzl(sqzlData)
}

window.updateInitialConsent = updateInitialConsent
